import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import {
  type EdgeId,
  type EdgeRecord,
  type NodeData,
  type NodeId,
  type NodeRecord,
  type NodeType,
  isNodeType,
} from '@ipfabric/utils';
import { getShowNodeDetailArgs } from 'components/DeviceView/helpers/getShowNodeDetailArgs';
import { showNodeDetail as showDeviceViewNodeDetail } from 'components/DeviceView/model/commands/showNodeDetail';
import { initPathLookupPathInspector } from 'components/Graph/commands/pathLookup/initPathLookupPathInspector';
import { option } from 'fp-ts';
import { constUndefined } from 'fp-ts/lib/function';
import { pipe } from 'fp-ts/lib/pipeable';
import type { GraphCommandFactory } from '../model/GraphCommandContext';
import { selectSnapshotToCompareId } from '../model/selectors/overlay/selectSnapshotToCompareId';
import type { EdgeStableDataRecord } from '../model/selectors/selectEdgesStableData';
import type {
  NodeStableData,
  NodeStableDataRecord,
} from '../model/selectors/selectNodesStableData';
import { hasNodePathInspectorDetail } from './pathLookup/hasNodePathInspectorDetail';

export interface ShowNodeDetailCommand {
  nodeId: NodeId;
  selectedIncomingEdgeId?: EdgeId;
  selectedOutgoingEdgeId?: EdgeId;
}

export const hasNodeDetail = (type: NodeType): boolean =>
  isNodeType(type) && type !== 'transit' && type !== 'cloud';

export const getParentNodeData = (
  nodes: NodeRecord | NodeStableDataRecord,
  edges: EdgeRecord | EdgeStableDataRecord,
  nodeData: NodeData | NodeStableData,
): NodeData | null => {
  const edge = Object.values(edges).find((e) => e.target === nodeData.id);
  // Node can highest in tree topology without parent node. So incoming edge doesn't exist.
  if (edge === undefined) {
    return null;
  }
  const foundParentNode = nodes[edge.source];
  if (foundParentNode === undefined) {
    stumble('no parent node found');
    return null;
  }
  return foundParentNode as NodeData;
};

export const showNodeDetail: GraphCommandFactory<ShowNodeDetailCommand> =
  (cmdContext, dispatch) => (command) => {
    const {
      graph: { edges, nodes },
    } = cmdContext.getState();
    pipe(
      nodes[command.nodeId],
      option.fromNullable,
      option.fold(constUndefined, async (nodeData) => {
        if (hasNodeDetail(nodeData.type)) {
          const { graphMetadata } = cmdContext.getState();
          const nodeHasPathInspectorDetail = hasNodePathInspectorDetail(
            nodeData,
            graphMetadata,
          );
          const snapshotToCompareId = selectSnapshotToCompareId(
            cmdContext.getState(),
          );
          cmdContext.getDeviceView().dispatch(
            showDeviceViewNodeDetail,
            // casting DeviceType already checked in hasNodeDetail
            getShowNodeDetailArgs(
              command.nodeId,
              nodeData,
              getParentNodeData(nodes, edges, nodeData),
              nodeHasPathInspectorDetail,
              nodeData.type,
              snapshotToCompareId,
            ),
          );
          if (nodeHasPathInspectorDetail) {
            // Wait for initialized device explorer entity
            await sleep(0);
            dispatch(initPathLookupPathInspector, {
              nodeSn: nodeData.sn,
              selectedIncomingEdgeId: command.selectedIncomingEdgeId,
              selectedOutgoingEdgeId: command.selectedOutgoingEdgeId,
            });
          }
        }
      }),
    );
  };
